import React from 'react';
import { createTheme, type Components, type Theme, type ThemeOptions } from '@mui/material/styles';
import { KALEIDOSCOPE, KALEIDOSCOPE_CHART } from './kaleidoscope';
import {
  CONTROL,
  DOMAIN_HEX,
  FONT,
  MOTION,
  RADIUS,
  STATUS_HEX,
  STATUS_INK,
  STATUS_TINT,
  TYPE,
} from '../design-system/foundation';

/**
 * MUI theme — built on design-system/foundation.ts.
 *
 * Palettes mirror the CSS variables in design-system/styles/index.css exactly
 * (MUI needs literal hex to compute contrast and hover tints). Every component
 * override below uses the CSS variables directly, so it is mode-agnostic: the
 * same override renders correctly in light and dark.
 */

const PALETTE = {
  dark: {
    background: '#0b0e16',
    paper: '#141824',
    card: '#1a2030',
    text: { primary: '#f5f7fc', secondary: '#b9c0d6', disabled: '#8d97b2' },
    divider: 'rgba(148, 163, 205, 0.14)',
    primary: { main: '#8b9eff', light: '#a9b6ff', dark: '#6c7bff', contrastText: '#0b0e16' },
    secondary: { main: '#a78bfa', light: '#c4b5fd', dark: '#7c4fd8', contrastText: '#0b0e16' },
    success: { main: STATUS_HEX.success, light: '#5ee6ac', dark: '#12a86a', contrastText: '#04140c' },
    warning: { main: STATUS_HEX.warning, light: '#ffcd4d', dark: '#c48f00', contrastText: '#1a1200' },
    error: { main: STATUS_HEX.error, light: '#f7849a', dark: '#d63a52', contrastText: '#ffffff' },
    info: { main: STATUS_HEX.info, light: '#7aa8f5', dark: '#2f6ad9', contrastText: '#ffffff' },
  },
  light: {
    background: '#f7f5ff',
    paper: '#ffffff',
    card: '#efedfb',
    text: { primary: '#191833', secondary: '#4d4873', disabled: '#8e89b5' },
    divider: 'rgba(76, 66, 150, 0.12)',
    primary: { main: '#5457e0', light: '#7b7ef0', dark: '#4142c4', contrastText: '#ffffff' },
    secondary: { main: '#7c4fd8', light: '#9d7be6', dark: '#6a45c9', contrastText: '#ffffff' },
    success: { main: '#0f8a5f', light: '#22d989', dark: '#0b7a52', contrastText: '#ffffff' },
    warning: { main: '#a86e00', light: '#f5b400', dark: '#8a5a00', contrastText: '#ffffff' },
    error: { main: '#d23a52', light: '#f2556a', dark: '#c02640', contrastText: '#ffffff' },
    info: { main: '#2f6ad9', light: '#4d8af0', dark: '#245bc7', contrastText: '#ffffff' },
  },
} as const;

/** Kept for existing imports. */
const japanesePalette = PALETTE;

const focusRing = {
  outline: '2px solid var(--accent)',
  outlineOffset: 2,
} as const;

/** Line icons for the notice banner (MuiAlert). */
const NOTE_ICON_PATHS: Record<'info' | 'success' | 'warning' | 'error', React.ReactNode[]> = {
  info: [
    React.createElement('circle', { key: 'c', cx: 12, cy: 12, r: 9 }),
    React.createElement('path', { key: 'p', d: 'M12 11v5.5M12 7.8v.1' }),
  ],
  success: [
    React.createElement('circle', { key: 'c', cx: 12, cy: 12, r: 9 }),
    React.createElement('path', { key: 'p', d: 'm8 12.3 2.8 2.8L16 9.5' }),
  ],
  warning: [
    React.createElement('path', { key: 't', d: 'M12 4 2.8 19.5h18.4z' }),
    React.createElement('path', { key: 'p', d: 'M12 10v4.5M12 17.3v.1' }),
  ],
  error: [
    React.createElement('circle', { key: 'c', cx: 12, cy: 12, r: 9 }),
    React.createElement('path', { key: 'p', d: 'M12 7.5v5.5M12 16.2v.1' }),
  ],
};

function noteIcon(kind: keyof typeof NOTE_ICON_PATHS) {
  return React.createElement(
    'svg',
    {
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.8,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      'aria-hidden': true,
    },
    ...NOTE_ICON_PATHS[kind],
  );
}

const components: Components<Omit<Theme, 'components'>> = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: 'var(--app-bg)',
        color: 'var(--cockpit-ink)',
        fontFamily: FONT.sans,
        fontSize: '15px',
      },
      '::selection': {
        backgroundColor: 'var(--accent-tint)',
      },
      '@media (prefers-reduced-motion: reduce)': {
        '*, *::before, *::after': {
          animationDuration: '0.001ms !important',
          animationIterationCount: '1 !important',
          transitionDuration: '0.001ms !important',
          scrollBehavior: 'auto !important',
        },
      },
    },
  },

  /* ── Buttons ─────────────────────────────────────────────────────── */
  MuiButtonBase: {
    defaultProps: { disableRipple: false },
    styleOverrides: {
      root: { '&.Mui-focusVisible': focusRing },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.875rem',
        letterSpacing: 0,
        lineHeight: 1.4,
        borderRadius: RADIUS.control,
        minHeight: CONTROL.button.md,
        padding: '8px 16px',
        whiteSpace: 'nowrap',
        transition: `background-color ${MOTION.fast}, border-color ${MOTION.fast}, color ${MOTION.fast}, box-shadow ${MOTION.fast}`,
      },
      sizeSmall: {
        minHeight: CONTROL.button.sm,
        padding: '4px 12px',
        fontSize: '0.8125rem',
      },
      sizeLarge: {
        minHeight: CONTROL.button.lg,
        padding: '10px 22px',
        fontSize: '0.9375rem',
      },
      outlined: {
        borderColor: 'var(--border-strong)',
        '&:hover': { borderColor: 'var(--cockpit-tertiary)', backgroundColor: 'var(--hover-bg)' },
      },
      outlinedInherit: {
        color: 'var(--cockpit-ink)',
      },
      text: {
        '&:hover': { backgroundColor: 'var(--hover-bg)' },
      },
      startIcon: { marginRight: 6 },
      endIcon: { marginLeft: 6 },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS.control,
        color: 'var(--cockpit-secondary)',
        '&:hover': { backgroundColor: 'var(--hover-bg)', color: 'var(--cockpit-ink)' },
      },
      sizeMedium: { padding: 8 },
      sizeSmall: { padding: 6 },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.8125rem',
        borderColor: 'var(--border-default)',
        color: 'var(--cockpit-secondary)',
        borderRadius: RADIUS.control,
        '&.Mui-selected': {
          backgroundColor: 'var(--selected-bg)',
          color: 'var(--cockpit-ink)',
          '&:hover': { backgroundColor: 'var(--selected-bg)' },
        },
      },
      sizeSmall: { minHeight: CONTROL.button.sm, padding: '4px 10px' },
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: { borderRadius: RADIUS.control },
    },
  },

  /* ── Chips ───────────────────────────────────────────────────────── */
  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        fontSize: '0.8125rem',
        height: CONTROL.chip.md,
        borderRadius: RADIUS.chip,
      },
      sizeSmall: {
        height: CONTROL.chip.sm,
        fontSize: '0.75rem',
      },
      label: { paddingLeft: 10, paddingRight: 10 },
      labelSmall: { paddingLeft: 8, paddingRight: 8 },
      outlined: {
        borderColor: 'var(--border-default)',
      },
      filled: {
        '&.MuiChip-colorDefault': {
          backgroundColor: 'var(--status-neutral-tint)',
          color: 'var(--cockpit-secondary)',
        },
      },
    },
  },

  /* ── Surfaces ────────────────────────────────────────────────────── */
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: 'var(--panel-bg)',
        color: 'var(--cockpit-ink)',
      },
      rounded: { borderRadius: RADIUS.card },
      outlined: {
        borderColor: 'var(--border-default)',
      },
      elevation0: { boxShadow: 'none' },
      elevation1: { boxShadow: 'var(--elevation-sm)' },
      elevation2: { boxShadow: 'var(--elevation-sm)' },
      elevation3: { boxShadow: 'var(--elevation-md)' },
      elevation4: { boxShadow: 'var(--elevation-md)' },
      elevation8: { boxShadow: 'var(--elevation-lg)', border: '1px solid var(--border-default)' },
      elevation16: { boxShadow: 'var(--elevation-lg)' },
      elevation24: { boxShadow: 'var(--elevation-lg)' },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS.card,
        backgroundColor: 'var(--panel-bg)',
        border: '1px solid var(--border-default)',
        boxShadow: 'var(--elevation-sm)',
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: { padding: 20, '&:last-child': { paddingBottom: 20 } },
    },
  },
  MuiAccordion: {
    defaultProps: { disableGutters: true, elevation: 0 },
    styleOverrides: {
      root: {
        backgroundColor: 'var(--panel-bg)',
        border: '1px solid var(--border-default)',
        borderRadius: RADIUS.card,
        '&::before': { display: 'none' },
        '&:not(:last-of-type)': { marginBottom: 8 },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: 'var(--panel-bg)',
        color: 'var(--cockpit-ink)',
        borderBottom: '1px solid var(--border-default)',
        boxShadow: 'none',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: 'var(--panel-bg)',
        borderColor: 'var(--border-default)',
        borderRadius: 0,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: RADIUS.dialog,
        border: '1px solid var(--border-default)',
        boxShadow: 'var(--elevation-lg)',
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: { ...TYPE.sectionTitle, padding: '20px 24px 8px' },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: { padding: '8px 24px 16px' },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: { padding: '12px 24px 20px', gap: 8, '& > :not(style) ~ :not(style)': { marginLeft: 0 } },
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      root: {
        '&:not(.MuiBackdrop-invisible)': { backgroundColor: 'var(--backdrop-bg)' },
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: { border: '1px solid var(--border-default)', boxShadow: 'var(--elevation-lg)' },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: { border: '1px solid var(--border-default)', boxShadow: 'var(--elevation-lg)' },
      list: { padding: 4 },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: '0.875rem',
        minHeight: 36,
        borderRadius: RADIUS.chip,
        '&:hover': { backgroundColor: 'var(--hover-bg)' },
        '&.Mui-selected': { backgroundColor: 'var(--selected-bg)' },
        '&.Mui-selected:hover': { backgroundColor: 'var(--selected-bg)' },
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: { borderColor: 'var(--border-subtle)' },
    },
  },

  /* ── Lists & navigation ──────────────────────────────────────────── */
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS.control,
        '&:hover': { backgroundColor: 'var(--hover-bg)' },
        '&.Mui-selected': {
          backgroundColor: 'var(--selected-bg)',
          '&:hover': { backgroundColor: 'var(--selected-bg)' },
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: { minHeight: CONTROL.tab },
      indicator: { height: 2, borderRadius: '2px 2px 0 0' },
      flexContainer: { gap: 4 },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        minHeight: CONTROL.tab,
        minWidth: 0,
        padding: '10px 12px',
        fontSize: '0.875rem',
        fontWeight: 500,
        textTransform: 'none',
        letterSpacing: 0,
        color: 'var(--cockpit-secondary)',
        '&:hover': { color: 'var(--cockpit-ink)' },
        '&.Mui-selected': { fontWeight: 600 },
        '& .MuiTab-iconWrapper': { fontSize: 18 },
      },
      labelIcon: { minHeight: CONTROL.tab, gap: 6, '& > .MuiTab-iconWrapper': { marginBottom: 0 } },
    },
  },
  MuiBreadcrumbs: {
    styleOverrides: {
      root: { fontSize: '0.8125rem', color: 'var(--cockpit-tertiary)' },
    },
  },

  /* ── Data display ────────────────────────────────────────────────── */
  MuiTableContainer: {
    styleOverrides: {
      root: { backgroundImage: 'none' },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        paddingTop: 12,
        paddingBottom: 12,
        fontSize: '0.875rem',
        borderColor: 'var(--border-subtle)',
      },
      head: {
        ...TYPE.label,
        fontWeight: 600,
        color: 'var(--cockpit-secondary)',
        paddingTop: 10,
        paddingBottom: 10,
      },
      sizeSmall: { paddingTop: 8, paddingBottom: 8 },
      stickyHeader: { backgroundColor: 'var(--panel-bg)' },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&.MuiTableRow-hover:hover': { backgroundColor: 'var(--hover-bg)' },
        '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'var(--selected-bg)' },
      },
    },
  },
  MuiTooltip: {
    defaultProps: { arrow: true },
    styleOverrides: {
      tooltip: {
        borderRadius: RADIUS.chip,
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: 1.45,
        padding: '6px 10px',
        backgroundColor: 'var(--tooltip-bg)',
        color: 'var(--tooltip-ink)',
        boxShadow: 'var(--elevation-md)',
      },
      arrow: { color: 'var(--tooltip-bg)' },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: { borderRadius: RADIUS.pill, backgroundColor: 'var(--border-subtle)' },
      bar: { borderRadius: RADIUS.pill },
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: { backgroundColor: 'var(--hover-bg)' },
      rounded: { borderRadius: RADIUS.control },
    },
  },
  MuiBadge: {
    styleOverrides: {
      badge: { fontWeight: 600, fontSize: '0.6875rem' },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: { fontWeight: 600 },
    },
  },

  /* ── Feedback ────────────────────────────────────────────────────── */
  MuiAlert: {
    defaultProps: {
      iconMapping: {
        info: noteIcon('info'),
        success: noteIcon('success'),
        warning: noteIcon('warning'),
        error: noteIcon('error'),
      },
    },
    styleOverrides: {
      root: {
        '--c': 'var(--note-info)',
        position: 'relative',
        isolation: 'isolate',
        alignItems: 'center',
        gap: 14,
        padding: '12px 14px 12px 12px',
        borderRadius: 16,
        fontFamily: "'Geist', system-ui, -apple-system, 'Segoe UI', sans-serif",
        fontSize: '14.5px',
        fontWeight: 500,
        lineHeight: 1.5,
        letterSpacing: '-0.01em',
        color: 'var(--cockpit-ink)',
        border: '1px solid color-mix(in srgb, var(--c) 32%, var(--note-line))',
        background:
          'radial-gradient(260px 90px at 0% 0%, color-mix(in srgb, var(--c) 20%, transparent), transparent 80%), color-mix(in srgb, var(--c) 8%, transparent)',
        animation: 'muiNoteIn .25s ease',
        '@keyframes muiNoteIn': { from: { opacity: 0, transform: 'translateY(-4px)' } },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          padding: 1,
          pointerEvents: 'none',
          opacity: 0.8,
          background: 'linear-gradient(115deg, color-mix(in srgb, var(--c) 80%, transparent), transparent 40%)',
          WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        },
        '& .MuiAlert-icon': {
          flex: 'none',
          width: 34,
          height: 34,
          margin: 0,
          padding: 0,
          borderRadius: 10,
          display: 'grid',
          placeItems: 'center',
          opacity: 1,
          color: 'var(--c)',
          background: 'color-mix(in srgb, var(--c) 15%, transparent)',
          border: '1px solid color-mix(in srgb, var(--c) 28%, transparent)',
          '& svg': { width: 18, height: 18 },
        },
        '& .MuiAlert-message': { flex: 1, minWidth: 0, padding: 0 },
        '& .MuiAlert-message a': {
          color: 'var(--note-link)',
          fontWeight: 650,
          textDecoration: 'underline',
          textUnderlineOffset: 3,
        },
        '& .MuiAlert-action': {
          margin: 0,
          padding: 0,
          '& .MuiIconButton-root': {
            width: 30,
            height: 30,
            borderRadius: 9,
            color: 'var(--dx-muted)',
            '&:hover': { background: 'color-mix(in srgb, var(--c) 14%, transparent)', color: 'var(--cockpit-ink)' },
            '& svg': { width: 15, height: 15 },
          },
        },
      },
      standardInfo: { '--c': 'var(--note-info)' },
      standardSuccess: { '--c': 'var(--note-ok)' },
      standardWarning: { '--c': 'var(--note-warn)' },
      standardError: { '--c': 'var(--note-bad)' },
      outlinedInfo: { '--c': 'var(--note-info)' },
      outlinedSuccess: { '--c': 'var(--note-ok)' },
      outlinedWarning: { '--c': 'var(--note-warn)' },
      outlinedError: { '--c': 'var(--note-bad)' },
    },
  },
  MuiAlertTitle: {
    styleOverrides: {
      root: { fontWeight: 600, marginBottom: 2, marginTop: 0 },
    },
  },
  MuiSnackbarContent: {
    styleOverrides: {
      root: { backgroundColor: 'var(--tooltip-bg)', color: 'var(--tooltip-ink)', borderRadius: RADIUS.control },
    },
  },

  /* ── Inputs ──────────────────────────────────────────────────────── */
  MuiTextField: {
    defaultProps: { size: 'small' },
  },
  MuiFormControl: {
    defaultProps: { size: 'small' },
  },
  MuiSelect: {
    defaultProps: { size: 'small' },
  },
  MuiAutocomplete: {
    defaultProps: { size: 'small' },
    styleOverrides: {
      paper: { border: '1px solid var(--border-default)', boxShadow: 'var(--elevation-lg)' },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS.control,
        fontSize: '0.875rem',
        backgroundColor: 'var(--panel-bg)',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-strong)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--cockpit-tertiary)' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
      },
      inputSizeSmall: { paddingTop: 9, paddingBottom: 9 },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: { fontSize: '0.875rem' },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: { fontSize: '0.75rem', marginLeft: 2 },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      label: { fontSize: '0.875rem' },
    },
  },
};

const baseTheme: ThemeOptions = {
  typography: {
    fontFamily: FONT.sans,
    fontSize: 14,
    h1: { ...TYPE.display },
    h2: { ...TYPE.pageTitle },
    h3: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.3 },
    h4: { ...TYPE.sectionTitle },
    h5: { fontSize: '1rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.4 },
    h6: { ...TYPE.subheading },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.45 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.45 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    caption: { fontSize: '0.8125rem', lineHeight: 1.45 },
    overline: { ...TYPE.overline },
    button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none' },
  },
  shape: {
    // sx `borderRadius: n` multiplies this. 8 keeps legacy `borderRadius: 1/1.5/2`
    // at 8/12/16px instead of the old 12/18/24px.
    borderRadius: 8,
  },
  components,
};

function buildTheme(mode: 'light' | 'dark') {
  const p = PALETTE[mode];
  return createTheme({
    ...baseTheme,
    palette: {
      mode,
      primary: { ...p.primary },
      secondary: { ...p.secondary },
      success: { ...p.success },
      warning: { ...p.warning },
      error: { ...p.error },
      info: { ...p.info },
      background: { default: p.background, paper: p.paper },
      text: { ...p.text },
      divider: p.divider,
      action: {
        active: p.text.secondary,
        hover: mode === 'dark' ? 'rgba(148, 163, 205, 0.08)' : 'rgba(76, 66, 150, 0.06)',
        selected: mode === 'dark' ? 'rgba(139, 158, 255, 0.14)' : 'rgba(84, 87, 224, 0.1)',
        disabled: p.text.disabled,
        disabledBackground: mode === 'dark' ? 'rgba(148, 163, 205, 0.12)' : 'rgba(76, 66, 150, 0.08)',
        focus: mode === 'dark' ? 'rgba(139, 158, 255, 0.2)' : 'rgba(84, 87, 224, 0.16)',
      },
    },
  });
}

export const darkTheme = buildTheme('dark');
export const lightTheme = buildTheme('light');

export const workerColors = {
  architect: DOMAIN_HEX.discover,
  developer: STATUS_HEX.info,
  sensei: DOMAIN_HEX.understand,
  diplomat: DOMAIN_HEX.change,
  forensic: DOMAIN_HEX.operate,
  steward: DOMAIN_HEX.discover,
  crystal: '#e879f9',
  healer: '#5ef0c4',
  security: DOMAIN_HEX.operate,
  test: STATUS_HEX.info,
  profiler: DOMAIN_HEX.understand,
};

export const chartColors = {
  primary: [...KALEIDOSCOPE_CHART.slice(0, 4)] as string[],
  success: [STATUS_HEX.success, '#2dd4a8', '#1fb896', '#159e80'],
  warning: [STATUS_HEX.warning, '#e89a30', '#d48220', '#b86a10'],
  error: [STATUS_HEX.error, '#ff5c7a', '#e04560', '#c03048'],
  purple: [DOMAIN_HEX.understand, '#7c6ae8', '#6a58d8', '#5848c8'],
  gradient: [DOMAIN_HEX.discover, DOMAIN_HEX.understand, DOMAIN_HEX.operate],
};

export { japanesePalette };

export const swarmColors = {
  quantum: KALEIDOSCOPE.discover,
  neural: KALEIDOSCOPE.understand,
  genesis: KALEIDOSCOPE.sky,
  sentinel: KALEIDOSCOPE.change,
  phantom: '#e879f9',
  void: KALEIDOSCOPE.understand,
  nexus: KALEIDOSCOPE.discover,
  echo: KALEIDOSCOPE.operate,
  drift: KALEIDOSCOPE.sky,
  pulse: KALEIDOSCOPE.operate,
  core: KALEIDOSCOPE.discover,
  edge: KALEIDOSCOPE.understand,
};

export {
  KALEIDOSCOPE,
  KALEIDOSCOPE_CHART,
  DOMAIN_COLORS,
  panelSx,
  glassPanelSx,
  domainGlowSx,
} from './kaleidoscope';
